import moment from 'moment';
import lodashSortBy from 'lodash/fp/sortBy';

import log from 'client/lib/log';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { DATA_TABLE_SOURCE_AGENT_LEADS } from 'common/dataTableConstants'; // eslint-disable-line max-len

import { LEAD_STATUS_ARCHIVED } from 'common/leadConstants';
import { USER_ROLE_ADMIN, USER_ROLE_APARTMENT_LOCATOR } from 'common/userRoles';

export const placeholderProfilePicture =
  'https://s3.amazonaws.com/media-profile-pictures/default.svg';

export const defaultApartmentLocator = {
  name: 'Sunroom Locators',
  secondaryPhone: '5127143550',
  profilePicture: 'https://s3.amazonaws.com/media-profile-pictures/aptlocator-sunroom.jpg',
  apartmentLocatorSlackChannel: 'aptleads-unassigned',
  roles: [USER_ROLE_ADMIN, USER_ROLE_APARTMENT_LOCATOR],
};

const requiredApartmentLocatorFields = ['apartmentLocatorSlackChannel', 'secondaryPhone'];

export function checkAndReportMissingAparmentLocatorFields(apartmentLocator) {
  if (
    apartmentLocator &&
    requiredApartmentLocatorFields.some((requiredField) => !apartmentLocator[requiredField])
  ) {
    const error = new Error('Apartment locator missing required fields.');
    error.name = 'SrApartmentLocatorMissingRequiredFieldsError';
    error.details = {
      apartmentLocator,
      requiredApartmentLocatorFields,
    };
    log.error(error);
    return error;
  }
}

export const APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE =
  'APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE';
export const APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE =
  'APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE';
export const APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE =
  'APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE'; // eslint-disable-line max-len
export const APARTMENT_LOCATOR_EDIT_LEAD = 'APARTMENT_LOCATOR_EDIT_LEAD';
export const APARTMENT_LOCATOR_ARCHIVE_LEAD = 'APARTMENT_LOCATOR_ARHIVE_LEAD';
export const APARTMENT_LOCATOR_UN_ARCHIVE_LEAD = 'APARTMENT_LOCATOR_UN_ARCHIVE_LEAD';

const RESULTS_FROM_OTHER_TABS = 'RESULTS_FROM_OTHER_TABS';

export const APARTMENT_LOCATOR_LEASE_SIGN_PERCENTAGE_THRESHOLD_RED = 2;
export const APARTMENT_LOCATOR_LEASE_SIGN_PERCENTAGE_THRESHOLD_YELLOW = 3;
export const APARTMENT_LOCATOR_LEASE_SIGN_PERCENTAGE_THRESHOLD_GREEN = 6;

export const APARTMENT_LOCATOR_LEASE_SIGN_VOLUME_THRESHOLD_RED = 2;
export const APARTMENT_LOCATOR_LEASE_SIGN_VOLUME_THRESHOLD_YELLOW = 3;
export const APARTMENT_LOCATOR_LEASE_SIGN_VOLUME_THRESHOLD_GREEN = 6;

export const APT_LEADERBOARD_CUT_OFF_DATE = momentWithAustinTimezone()
  .month('February')
  .year(2019)
  .toISOString();

export function getLeadDataTableSettingsFromProps({
  currentUser,
  leadStatus,
  searchText,
  sortCriteria,
  items,
  userMoveInDateRange,
}) {
  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_AGENT_LEADS,
    viewId: `${leadStatus}-${sortCriteria}`,
    filter: {
      apartmentLocatorUserId: currentUser.id,
      'apartmentLocatorLeads.status': leadStatus,
    },
    sort: {
      [`apartmentLocatorLeads.${leadStatus}Date`]: 'desc',
    },
    count: 8,
    skip: (items || []).length,
    customParams: {
      agentLeads: {
        status: leadStatus,
      },
    },
  };

  if (userMoveInDateRange) {
    dataTableSettings.customParams.agentLeads.userMoveInDateRange = userMoveInDateRange;
    dataTableSettings.viewId = `${dataTableSettings.viewId}-moveDate_${userMoveInDateRange.join(
      '_',
    )}`;
    delete dataTableSettings.sort;
    if (userMoveInDateRange.length === 2) {
      dataTableSettings.count = 1000;
    }
  }

  if (searchText) {
    delete dataTableSettings.filter['apartmentLocatorLeads.status'];
    dataTableSettings.customParams.agentLeads.searchText = searchText;
    dataTableSettings.viewId = `${dataTableSettings.viewId}-search_${searchText}`;
  }

  return dataTableSettings;
}

function getSectionTitle({ sectionId, sortCriteria }) {
  if (RESULTS_FROM_OTHER_TABS === sectionId) {
    return 'results from other tabs';
  }
  if (
    sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE ||
    sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE
  ) {
    return moment(sectionId, 'MMM YYYY').format('MMMM YYYY');
  }

  const date = momentWithAustinTimezone(sectionId, 'L');
  switch (date.diff(new Date(), 'days')) {
    case 0:
      return 'today';
    case -1:
      return 'yesterday';
    default:
      return date.format('MMM D');
  }
}

export function createLeadTableSections({ leadStatus, items, sortCriteria, leadsRemovedFromStep }) {
  const leadStatusDate = `${leadStatus}Date`;

  const itemsSorted =
    sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_STATUS_DATE
      ? lodashSortBy(leadStatusDate)(items).reverse()
      : sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE
      ? lodashSortBy('lease.startDate')(items)
      : lodashSortBy('renter.moveInDate')(items);

  // Group leads by date
  const leadsByDate = itemsSorted.reduce((sections, lead) => {
    let groupId = null;

    if (leadStatus !== lead.status && !leadsRemovedFromStep.includes(lead.renter.id)) {
      groupId = RESULTS_FROM_OTHER_TABS;
    } else if (sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_MOVE_DATE) {
      groupId = momentWithAustinTimezone(lead.renter.moveInDate).format('MMM YYYY');
    } else if (sortCriteria === APARTMENT_LOCATOR_LEADS_SORT_BY_LEASE_START_DATE) {
      groupId = momentWithAustinTimezone(lead.lease.startDate).format('MMM YYYY');
    } else {
      groupId = momentWithAustinTimezone(lead[leadStatusDate]).format('L');
    }

    if (!sections[groupId]) {
      sections[groupId] = [];
    }
    sections[groupId].push(lead);
    return sections;
  }, {});

  // Sort items in each section
  Object.keys(leadsByDate).forEach((dateFormat) => {
    if (dateFormat === RESULTS_FROM_OTHER_TABS) {
      leadsByDate[dateFormat] = lodashSortBy('renter.name')(leadsByDate[dateFormat]);
    }
  });

  // Create sections
  const sections = Object.keys(leadsByDate)
    .reduce((sections, sectionId) => {
      return [
        ...sections,
        {
          id: sectionId,
          items: leadsByDate[sectionId],
          title: getSectionTitle({ sectionId, sortCriteria }),
        },
      ];
    }, [])
    .sort((sectionA, sectionB) => {
      // Move results from other tabs to the bottom
      if (sectionA.id === RESULTS_FROM_OTHER_TABS) {
        return 1;
      }
      if (sectionB.id === RESULTS_FROM_OTHER_TABS) {
        return -1;
      }
      return 0;
    });

  return sections;
}

export function getLeadsUpdated({ leadsUpdated, leadStatus }) {
  return Object.keys(leadsUpdated).reduce(
    (memo, renterUserId) => {
      const renter = leadsUpdated[renterUserId];

      if (renter.fromLeadStatus === leadStatus && leadStatus !== LEAD_STATUS_ARCHIVED) {
        memo.leadsRemovedFromStep.push(parseInt(renterUserId, 10));
      } else if (renter.toLeadStatus === leadStatus) {
        memo.leadsAddedToStep.push(parseInt(renterUserId, 10));
      }

      return memo;
    },
    { leadsAddedToStep: [], leadsRemovedFromStep: [] },
  );
}
